import Chaplin from 'chaplin';
import utils from 'lib/utils';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';
import { LocalStorageService, SessionStorageService } from 'services/data-handler-services/web-storage-service';
import StateParser, { getExpressionVariables } from 'services/state-handler-services/state-parser';
import StateFormatService from 'services/state-handler-services/state-format-service';

// StateService
//
// The course's variable store: values, the expressions derived from them, where
// they persist, and who to tell when they change.
//
// This is the state half of what the twine block used to do. Twine kept a
// `varObj`, hydrated it from a hand-written `dataVars` list at play(), mutated
// it by walking a node graph, and pushed it at the DOM through `domVars`. The
// three lists meant every variable was declared two or three times, and because
// nodes are an imperative sequence, authors had to hand-order
// their `vars` array and re-run calculations defensively.
//
// Here a variable is declared once, and nothing about it is declared twice:
//
// * Dependencies come from the expression - `parse(expr).variables()` - so there
//   is no `variables:` list and no ordering for an author to get wrong. Setting
//   an input recomputes everything downstream of it, in dependency order.
// * Reads resolve lazily from the variable's service on a miss, so there is no
//   read list. This is the whole of what `dataVars` did in the read direction.
// * Values stay in their computed type. Display formatting belongs to whoever
//   renders the value (see StateFormatService), which is what removes the
//   `$total` / `$totalDisplay` pairs from content.
// * A missing variable is reported by name. It never silently becomes 0.
//
// Writes are per-key and coalesced: we merge only the keys this service actually
// computed into the stored object, never the keys we merely read, and never an
// undefined value. Twine's `setDataVars` did a whole-object replace on every
// node transition using its page's full `dataVars` list, so two pages saving
// near each other could drop each other's keys.

// Where a variable lives. Named to match the rest of the SDK - the `data` block
// and the `switch` block's `source` both call this a `service`, with the same
// values.
//
// `enrollment` is not a single store, it is a cascade. EF.enrollment keeps the
// value in memory and in localStorage always, and additionally on the server
// when the course is not running in local mode. Reads come back through the same
// tiers, back-filling as they go. That is the usual behaviour for a course,
// but it means content behaves differently in a course build and in production,
// so it has to be asked for, not inherited.
//
// `memory` is the lifetime of whatever declared it - nothing is persisted.
// `page` is accepted as an alias, because that is the lifetime it describes.
const SERVICES = ['enrollment', 'local', 'session', 'memory'];
const SERVICE_ALIASES = { page: 'memory' };

// Existing course data all lives under this key, so it stays the default.
const DEFAULT_NAMESPACE = 'twineData';

// What to do when an expression reads a variable nothing has produced.
const ON_MISSING = ['error', 'warn', 'zero'];
const DEFAULT_ON_MISSING = 'warn';

const StateService = {
  // name -> current value
  values: {},

  // name -> { expression, dependencies, format, precision, currency, service, ... }
  definitions: {},

  // dependency name -> Set of definition names that read it, for recompute
  dependents: {},

  // name -> service. A name with no entry here is never persisted and is never
  // read back from storage - there is no default, so nothing lands somewhere
  // the content did not ask for.
  services: {},

  // name -> namespace, and name -> stored key, for the few values that belong
  // to another block's storage contract, outside this course's own
  // namespace. Empty for everything else, which reads the course default and
  // stores under the variable's own name.
  namespaces: {},
  keys: {},

  // Names read from storage but never written back. A form block that keeps
  // its fields under its own key already persists them; a calculation that
  // reads them should not become a second writer of the same object.
  readOnly: new Set(),

  // '<service>|<namespace>' -> Set of names written but not yet flushed. Keyed
  // by both, because each namespace is a separate stored object that has to be
  // read, merged and written on its own.
  pending: {},

  // Missing variables already reported, so a value referenced on every
  // recompute is logged once, not once per pass.
  reported: {},

  // Names a learner has explicitly cleared. Without this the lazy read undoes
  // the clear on the spot: `values[name]` is undefined, so the next `get` goes
  // to storage, finds the value that has not been flushed away yet, and puts it
  // back. An absent value and a cleared one are different things.
  cleared: new Set(),

  namespace: DEFAULT_NAMESPACE,
  onMissing: DEFAULT_ON_MISSING,
  flushTimeout: null,

  // Setup

  // `namespace` and `onMissing` are course-wide settings. Where a value is
  // stored is not - that belongs to whatever declares the value, so it is
  // passed to `define`/`assignService`.
  configure: function (options = {}) {
    if (options.namespace && options.namespace !== this.namespace) {
      // Two blocks asking for different namespaces would send values somewhere
      // the other one can't read.
      if (Object.keys(this.services).length > 0) {
        console.error(
          `StateService: namespace is already '${this.namespace}' and something asked for ` +
            `'${options.namespace}'. Values are stored under one namespace per course.`
        );
      } else {
        this.namespace = options.namespace;
      }
    }
    if (options.onMissing) {
      if (!ON_MISSING.includes(options.onMissing)) {
        console.error(`StateService: unknown onMissing '${options.onMissing}', keeping '${this.onMissing}'`);
      } else {
        this.onMissing = options.onMissing;
      }
    }
  },

  // Tests use this; the runtime clears per-page definitions
  // through `undefine` from the owning block's dispose instead, because blocks
  // register during page initialize and a page-level reset would race them.
  reset: function () {
    this.values = {};
    this.definitions = {};
    this.dependents = {};
    this.services = {};
    this.namespaces = {};
    this.keys = {};
    this.readOnly = new Set();
    this.pending = {};
    this.reported = {};
    this.cleared = new Set();
    this.namespace = DEFAULT_NAMESPACE;
    this.onMissing = DEFAULT_ON_MISSING;
    clearTimeout(this.flushTimeout);
    this.flushTimeout = null;
  },

  // Names
  //
  // Twine wrote `$name`; new content writes `name`. They are the same variable.
  // We key on the bare name and expose both spellings to expressions, so
  // expressions carried over from twine evaluate unchanged.

  normalizeName: function (name) {
    return typeof name === 'string' && name.charAt(0) === '$' ? name.slice(1) : name;
  },

  // Definitions

  // Register a derived variable.
  //
  // definition:
  //   expression  - an expression over other variables
  //   when        - a condition ladder, as an alternative to `expression`:
  //                 [{ if, value | expression }, ..., { otherwise: { value } }]
  //   format      - display projection name (currency, percent, decimal, integer)
  //   precision   - decimal places for the format
  //   currency    - ISO code for the currency format
  //   service     - where to persist; required for the value to be stored at all
  //   namespace   - stored object to write into, when the value belongs to
  //                 something other than this course's own namespace
  //   key         - name to store it under, when that differs from the
  //                 variable's name
  //   persist     - false to keep the value out of storage entirely
  define: function (name, definition = {}) {
    const key = this.normalizeName(name);
    if (!key) {
      console.error('StateService: cannot define a variable without a name');
      return;
    }

    const dependencies = this.getDependencies(definition);
    this.reportMalformedBranches(key, definition);

    // Re-registering the same definition is normal: a `switch` with
    // `auto_refresh` re-renders its children, and each one initializes again.
    // Registering a different one under a name already taken is not, because
    // the second block wins silently and the first block's page is wrong.
    // Report it, as a conflicting service or namespace does.
    const existing = this.definitions[key];
    if (existing && !this.isSameDefinition(existing, definition)) {
      console.error(
        `StateService: '${key}' is already computed by something else. Two blocks computing one ` +
          'variable means whichever runs last wins.'
      );
    }

    this.definitions[key] = Object.assign({}, definition, { name: key, dependencies });

    if (definition.service) {
      this.assignService(key, definition.service);
    }
    if (definition.namespace || definition.key) {
      this.assignStorage(key, definition);
    }

    dependencies.forEach(dependency => {
      this.dependents[dependency] = this.dependents[dependency] || new Set();
      this.dependents[dependency].add(key);
    });
  },

  // A branch says when it applies - `if`, or `otherwise` for the fallback - and
  // what the value is. A branch with neither condition is unreachable, and
  // evaluating it silently would leave the author looking at a value that is
  // simply never produced.
  reportMalformedBranches: function (key, definition) {
    (definition.when || []).forEach((branch, index) => {
      if (branch.if !== undefined || branch.otherwise !== undefined) {
        return;
      }
      console.error(
        `StateService: branch ${index + 1} of '${key}' has neither 'if' nor 'otherwise', so it can ` +
          "never apply. Give it a condition, or make it the 'otherwise' branch."
      );
    });
  },

  // Whether two registrations say the same thing. Compares what an author
  // wrote, not the derived name and dependency list.
  isSameDefinition: function (existing, incoming) {
    const shape = definition =>
      JSON.stringify([
        definition.expression,
        definition.when,
        definition.service,
        definition.namespace,
        definition.key,
        definition.persist,
        definition.format,
        definition.precision,
        definition.currency,
      ]);
    return shape(existing) === shape(incoming);
  },

  defineAll: function (definitions = {}) {
    Object.entries(definitions).forEach(([name, definition]) => this.define(name, definition));
  },

  // Say where one or more variables live.
  //
  // Whatever declares a variable is responsible for this - a block assigns its
  // own service to the values it computes and to the inputs it reads, so every
  // name it owns has an explicit home. A name with no service assigned is never
  // written and is never read back from storage.
  assignService: function (names, service) {
    const resolved = this.normalizeService(service);
    if (!resolved) {
      console.error(`StateService: unknown service '${service}'. Expected one of ${SERVICES.join(', ')}.`);
      return;
    }

    (Array.isArray(names) ? names : [names]).forEach(name => {
      const key = this.normalizeName(name);
      if (!key) {
        return;
      }
      const existing = this.services[key];
      if (existing && existing !== resolved) {
        console.error(
          `StateService: '${key}' is already stored in '${existing}' and something asked for ` +
            `'${resolved}'. Keeping '${existing}'.`
        );
        return;
      }
      this.services[key] = resolved;
    });
  },

  // Send one variable somewhere other than the course's own namespace.
  //
  // Almost nothing needs this. It exists for values that are part of another
  // block's storage contract - ach-tools writes debt counts to
  // `enrollment.debts1.credit-card-debt-count` because a `form` block reads
  // them back from there - where the namespace and the stored key are both
  // fixed by the reader, not by the calculation.
  assignStorage: function (name, options = {}) {
    const key = this.normalizeName(name);
    if (!key) {
      return;
    }
    if (options.persist === false) {
      this.readOnly.add(key);
    }

    [
      ['namespaces', options.namespace, 'namespace'],
      ['keys', options.key, 'key'],
    ].forEach(([bucket, value, label]) => {
      if (!value) {
        return;
      }
      const existing = this[bucket][key];
      if (existing && existing !== value) {
        // Two blocks storing one variable in two places means whichever reads
        // it second gets the wrong answer. Report it instead of letting the
        // last one win.
        console.error(
          `StateService: '${key}' already has ${label} '${existing}' and something asked for ` +
            `'${value}'. Keeping '${existing}'.`
        );
        return;
      }
      this[bucket][key] = value;
    });
  },

  // The stored object a variable lives in, and the key it lives under. Both
  // default to the course-wide answer, which is what everything but a storage
  // contract with another block needs.
  getNamespace: function (name) {
    return this.namespaces[this.normalizeName(name)] || this.namespace;
  },

  getStorageKey: function (name) {
    const key = this.normalizeName(name);
    return this.keys[key] || key;
  },

  // `page` is accepted as an alias for `memory` - it describes the lifetime
  // more than the store, and both readings are reasonable.
  normalizeService: function (service) {
    const resolved = SERVICE_ALIASES[service] || service;
    return SERVICES.includes(resolved) ? resolved : undefined;
  },

  // Drop definitions when the block that declared them goes away, so the next
  // page doesn't recompute expressions against a DOM that no longer exists.
  // Persisted values survive - they belong to the enrollment, not the page.
  undefine: function (names) {
    const list = Array.isArray(names) ? names : [names];
    list.forEach(name => {
      const key = this.normalizeName(name);
      const definition = this.definitions[key];
      if (!definition) {
        return;
      }
      definition.dependencies.forEach(dependency => {
        this.dependents[dependency]?.delete(key);
        // Drop the entry once nothing reads the name, so the graph shows what
        // is actually there - `getSnapshot` and any debugging off it are only
        // as readable as this map.
        if (this.dependents[dependency]?.size === 0) {
          delete this.dependents[dependency];
        }
      });
      const service = this.services[key];
      delete this.definitions[key];
      delete this.services[key];
      delete this.namespaces[key];
      delete this.keys[key];
      this.readOnly.delete(key);
      this.cleared.delete(key);
      // A value that was never persisted has nowhere to come back from, so it
      // goes when its definition does.
      if (service === 'memory' || service === undefined) {
        delete this.values[key];
      }
    });
  },

  // Remember that a value was cleared, or forget it once one arrives.
  trackCleared: function (key, value) {
    if (value === undefined) {
      this.cleared.add(key);
    } else {
      this.cleared.delete(key);
    }
  },

  // Give up the storage assignment for names a block owned but did not compute
  // - its inputs. `undefine` cannot do this, because an input has no
  // definition to key off.
  //
  // A name still read by some other definition is left alone: two blocks on one
  // page can share an input, and the one that disposes first must not take the
  // other's storage with it.
  release: function (names) {
    const list = Array.isArray(names) ? names : [names];
    list.forEach(name => {
      const key = this.normalizeName(name);
      if (!key || this.definitions[key]) {
        return;
      }
      const stillRead = Object.values(this.definitions).some(definition =>
        (definition.dependencies || []).includes(key)
      );
      if (stillRead) {
        return;
      }

      const service = this.services[key];
      delete this.services[key];
      delete this.namespaces[key];
      delete this.keys[key];
      this.readOnly.delete(key);
      this.cleared.delete(key);
      if (service === 'memory' || service === undefined) {
        delete this.values[key];
      }
    });
  },

  // Every variable an expression or condition ladder reads. Parsing is the only
  // source of truth for this - an author never lists dependencies.
  getDependencies: function (definition) {
    const expressions = [];

    if (definition.expression) {
      expressions.push(definition.expression);
    }
    (definition.when || []).forEach(branch => {
      if (branch.if) {
        expressions.push(branch.if);
      }
      const result = branch.otherwise === undefined ? branch : branch.otherwise;
      if (result.expression) {
        expressions.push(result.expression);
      }
    });

    const names = new Set();
    expressions.forEach(expression => {
      let variables;
      try {
        variables = getExpressionVariables(expression);
      } catch (error) {
        // A malformed expression is an authoring error. Report it now, at
        // definition time, once, not on every recompute.
        console.error(`StateService: could not parse expression '${expression}': ${error.message}`);
        return;
      }
      variables.forEach(variable => names.add(this.normalizeName(variable)));
    });

    return [...names];
  },

  // Reading

  // Whether a variable has a value at all. This resolves from storage the same
  // way `get` does. The two have to agree: a `has` that returned false for a
  // value `get` would return once let a block's default overwrite a stored
  // answer from an earlier visit.
  has: function (name) {
    return this.get(name) !== undefined;
  },

  // A variable's current value, resolving it from storage the first time it is
  // asked for. The lazy read removes the need for twine's `dataVars` read
  // list: an expression referencing a value an earlier page saved just works.
  get: function (name) {
    const key = this.normalizeName(name);
    if (this.values[key] !== undefined) {
      return this.values[key];
    }
    if (this.cleared.has(key)) {
      return undefined;
    }

    const stored = this.readStored(key);
    if (stored !== undefined) {
      this.values[key] = stored;
    }
    return this.values[key];
  },

  getAll: function () {
    return Object.assign({}, this.values);
  },

  // The display projection of a variable, using the format from its definition
  // unless the caller overrides it. Binding uses this; nothing stores it.
  getFormatted: function (name, overrides = {}) {
    const key = this.normalizeName(name);
    const definition = this.definitions[key] || {};
    const format = overrides.format !== undefined ? overrides.format : definition.format;

    return StateFormatService.format(this.get(key), format, {
      precision: overrides.precision !== undefined ? overrides.precision : definition.precision,
      currency: overrides.currency !== undefined ? overrides.currency : definition.currency,
      locale: overrides.locale,
    });
  },

  // Writing

  // Set an input value and recompute everything downstream of it. Returns the
  // names that actually changed, which is empty when the value was already
  // what it is - that check is what stops a field:change echo from cascading.
  set: function (name, value, options = {}) {
    const key = this.normalizeName(name);
    // Compared against `get`, not against memory. A returning learner's value
    // may still be sitting in storage unread, and clearing a field has to
    // remove it - against memory alone, undefined equals undefined and the
    // clear is a no-op that the next lazy read then undoes.
    if (this.isEqual(this.get(key), value)) {
      return [];
    }

    this.values[key] = value;
    this.trackCleared(key, value);
    this.markPending(key);

    const changed = [key].concat(this.recomputeDependents([key]));
    this.publishChange(changed);
    this.scheduleFlush();
    if (options.flush) {
      this.flush();
    }
    return changed;
  },

  // Set several inputs as one change, so dependents recompute once against the
  // complete new input set, not once per input.
  setAll: function (values = {}, options = {}) {
    const seeded = [];
    Object.entries(values).forEach(([name, value]) => {
      const key = this.normalizeName(name);
      if (this.isEqual(this.get(key), value)) {
        return;
      }
      this.values[key] = value;
      this.trackCleared(key, value);
      this.markPending(key);
      seeded.push(key);
    });

    if (seeded.length === 0) {
      return [];
    }

    const changed = seeded.concat(this.recomputeDependents(seeded));
    this.publishChange(changed);
    this.scheduleFlush();
    if (options.flush) {
      this.flush();
    }
    return changed;
  },

  // Recomputation

  // Evaluate every definition downstream of the given names, in dependency
  // order, and return the ones whose value changed.
  recomputeDependents: function (names) {
    const affected = this.collectDependents(names);
    if (affected.size === 0) {
      return [];
    }

    const changed = [];
    this.sortByDependency(affected).forEach(name => {
      if (this.recompute(name)) {
        changed.push(name);
      }
    });
    return changed;
  },

  // Evaluate the named definitions and everything downstream of them, in
  // dependency order. Used for a block's first pass, where a calculation that
  // reads no inputs at all - a constant, or one fed only by other calculations -
  // still has to be computed once.
  recomputeAll: function (names) {
    const affected = this.collectDependents(names);
    names.forEach(name => {
      if (this.definitions[this.normalizeName(name)]) {
        affected.add(this.normalizeName(name));
      }
    });
    if (affected.size === 0) {
      return [];
    }

    const changed = [];
    this.sortByDependency(affected).forEach(name => {
      if (this.recompute(name)) {
        changed.push(name);
      }
    });
    return changed;
  },

  // Everything transitively reading the given names.
  collectDependents: function (names) {
    const affected = new Set();
    const queue = [...names];

    while (queue.length > 0) {
      const name = queue.shift();
      (this.dependents[name] ? [...this.dependents[name]] : []).forEach(dependent => {
        if (affected.has(dependent)) {
          return;
        }
        affected.add(dependent);
        queue.push(dependent);
      });
    }
    return affected;
  },

  // Kahn's algorithm over the affected subgraph, so a definition is evaluated
  // only once its inputs within the same pass are up to date. This replaces the
  // hand-curated ordering twine content needed - and the duplicate
  // recalculations authors added when the ordering was hard to reason about.
  sortByDependency: function (affected) {
    const remaining = new Map();
    affected.forEach(name => {
      const dependencies = (this.definitions[name]?.dependencies || []).filter(
        dependency => affected.has(dependency) && dependency !== name
      );
      remaining.set(name, new Set(dependencies));
    });

    const sorted = [];
    while (remaining.size > 0) {
      const ready = [...remaining.keys()].filter(name => remaining.get(name).size === 0);

      if (ready.length === 0) {
        // A cycle. Report it and evaluate what is left in declaration order so
        // the page still renders something.
        console.error(`StateService: circular dependency between ${[...remaining.keys()].join(', ')}`);
        sorted.push(...remaining.keys());
        break;
      }

      ready.forEach(name => {
        sorted.push(name);
        remaining.delete(name);
      });
      remaining.forEach(dependencies => ready.forEach(name => dependencies.delete(name)));
    }

    return sorted;
  },

  // Evaluate one definition. Returns whether its value changed.
  recompute: function (name) {
    const definition = this.definitions[name];
    if (!definition) {
      return false;
    }

    const scope = this.buildScope(definition);
    if (scope === undefined) {
      // Missing inputs under `onMissing: error` - leave the previous value in
      // place. Overwriting good data with a guess is worse than leaving it.
      return false;
    }

    const value = definition.when
      ? this.evaluateLadder(definition, scope)
      : this.evaluate(definition.expression, scope);

    // NaN is arithmetic that had nothing to work with - a string where a
    // number was expected, a division by an unset value. Storing it would put
    // `null` in the enrollment and "NaN" on the page, so it is treated like an
    // expression that failed: the previous value stays.
    if (
      value === undefined ||
      (typeof value === 'number' && Number.isNaN(value)) ||
      this.isEqual(this.values[name], value)
    ) {
      return false;
    }

    this.values[name] = value;
    this.cleared.delete(name);
    this.markPending(name);
    return true;
  },

  // A condition ladder. The first branch whose `if` holds supplies the value,
  // and `otherwise` is the fallback. Every branch carries its result the same
  // way: `value` is a literal as YAML typed it, `expression` is evaluated.
  evaluateLadder: function (definition, scope) {
    for (const branch of definition.when) {
      // A branch carries a condition and a result. `if` is the condition;
      // `otherwise` is the fallback, and holds its result in the same shape so
      // every branch reads the same way.
      const result = branch.otherwise === undefined ? branch : branch.otherwise;
      if (branch.otherwise === undefined && !this.evaluate(branch.if, scope)) {
        continue;
      }

      // `expression` is evaluated, `value` is taken as YAML typed it. Only one
      // of the two is meaningful on a branch; `expression` wins if both appear.
      if (result.expression !== undefined) {
        return this.evaluate(result.expression, scope);
      }
      return result.value;
    }
    return undefined;
  },

  // Evaluate an expression against a scope. A failure is reported and yields
  // undefined, which leaves the variable at its previous value - one bad
  // expression can't take the rest of the page's values down with it, which is
  // what happened when twine's handleVars threw mid-array.
  evaluate: function (expression, scope) {
    if (expression === undefined || expression === null) {
      return undefined;
    }

    const values = scope || this.buildScope({ dependencies: this.getDependencies({ expression }) }) || {};

    try {
      return StateParser.evaluate(expression, values);
    } catch (error) {
      console.error(`StateService: expression '${expression}' failed to evaluate: ${error.message}`);
      return undefined;
    }
  },

  // Build the evaluation scope for a definition, applying the missing-variable
  // policy. Returns undefined when the policy is `error` and something is
  // missing, to signal "don't evaluate this".
  //
  // Each dependency is exposed under both `name` and `$name` so expressions in
  // either dialect resolve without the expression text being rewritten.
  buildScope: function (definition, options = {}) {
    const scope = {};
    const missing = [];

    (definition.dependencies || []).forEach(dependency => {
      const value = this.get(dependency);
      if (value === undefined) {
        missing.push(dependency);
        scope[dependency] = 0;
      } else {
        scope[dependency] = this.toScopeValue(value);
      }
      scope[`$${dependency}`] = scope[dependency];
    });

    // `quiet` is for conditions, not calculations. A condition over a
    // variable nothing has produced yet is the normal state of a page the
    // learner has not filled in - it means "not yet", not "an author made a
    // mistake" - so it evaluates falsy without a report.
    if (missing.length > 0 && !options.quiet) {
      this.reportMissing(definition.name, missing);
      if (this.onMissing === 'error') {
        return undefined;
      }
    }

    return scope;
  },

  // What an expression sees for a stored value.
  //
  // Twine stored every bare literal as a string - `$sales=450` put "450" in
  // the enrollment - and then pasted the text back into the expression, so it
  // parsed as the number 450. Values twine wrote are still in every learner's
  // enrollment and are read back here as those strings; a form block stores
  // its fields as strings too. `"450" + 100` is null to the parser, so a
  // numeric string is a number in scope. The stored value is not touched.
  toScopeValue: function (value) {
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return value;
  },

  // Tell the author which variable is missing, once per variable.
  //
  // In a course build this is loud and per-variable, because that is where an
  // authoring mistake should surface. In production it is a single line, since
  // a learner's console is not a debugging surface and the page has already
  // degraded gracefully by this point.
  reportMissing: function (name, missing) {
    if (this.onMissing === 'zero') {
      return;
    }

    const unreported = missing.filter(dependency => !this.reported[dependency]);
    if (unreported.length === 0) {
      return;
    }
    unreported.forEach(dependency => {
      this.reported[dependency] = true;
    });

    const target = name ? ` while computing '${name}'` : '';
    if (this.isDevEnv()) {
      console.warn(
        `StateService: no value for ${unreported.map(m => `'${m}'`).join(', ')}${target}. ` +
          `Using 0. Check that an earlier page or an input produces ${unreported.length > 1 ? 'them' : 'it'}.`
      );
      return;
    }
    console.error(`StateService: missing ${unreported.join(', ')}${target}`);
  },

  // `utils.isDevEnv` reads `EF.config`, which isn't populated until the course
  // boots. A missing variable is most likely early in a page's life, so the
  // diagnostic has to tolerate being asked before then: reporting a warning
  // must never be what throws.
  isDevEnv: function () {
    try {
      return utils.isDevEnv();
    } catch (error) {
      return false;
    }
  },

  // Change notification

  publishChange: function (names) {
    if (names.length === 0) {
      return;
    }
    const values = {};
    names.forEach(name => {
      values[name] = this.values[name];
    });
    Chaplin.mediator.publish('state:changed', { names, values });
  },

  // Watching

  // Derive something from state and keep it derived.
  //
  // This is the pull side of the design. `StateBindingService` does it for a
  // value a block displays; `watch` does it for a condition a block renders
  // from - whether a button is enabled, whether a container is shown. Nothing
  // here reaches out to a block; the block reads.
  //
  // The handler is called once immediately with the current result, so a block
  // never has to write its own "and also on load" path. That is what twine's
  // `page:stateChange` + `$flagAlreadyTrue` pairs were for, and it is not
  // needed here because state does not record how it got its value. After
  // that, the handler is called only when the result changes.
  //
  // Returns the current result, and unsubscribes with the view.
  watch: function (view, expression, handler) {
    if (!expression || typeof handler !== 'function') {
      console.error('StateService: watch needs an expression and a handler');
      return undefined;
    }

    const dependencies = this.getDependencies({ expression });
    const evaluate = () => this.evaluate(expression, this.buildScope({ dependencies }, { quiet: true }));

    let last = evaluate();
    handler(last);

    if (view && typeof view.subscribeEvent === 'function') {
      view.subscribeEvent('state:changed', ({ names }) => {
        if (!names.some(name => dependencies.includes(name))) {
          return;
        }
        const next = evaluate();
        if (this.isEqual(next, last)) {
          return;
        }
        last = next;
        handler(next);
      });
    }

    return last;
  },

  // Persistence

  // Where a variable is stored, or undefined if nothing assigned it.
  getService: function (name) {
    return this.services[this.normalizeName(name)];
  },

  getStore: function (service) {
    const stores = {
      enrollment: EnrollmentDataService,
      session: SessionStorageService,
      local: LocalStorageService,
    };
    return stores[service];
  },

  // Read a single variable out of its service's stored namespace. A variable
  // with no service has nowhere to be read from: a value only comes out of
  // storage if it was assigned a service.
  readStored: function (name) {
    const store = this.getStore(this.getService(name));
    if (!store) {
      return undefined;
    }

    try {
      const stored = store.getByDotString(this.getNamespace(name));
      return stored ? stored[this.getStorageKey(name)] : undefined;
    } catch (error) {
      console.error(`StateService: could not read '${name}' from storage: ${error.message}`);
      return undefined;
    }
  },

  // Queue a value for the next flush. A variable with no service, one held in
  // memory, or one the author excluded with `persist: false`, never reaches
  // storage.
  markPending: function (name) {
    if (this.definitions[name]?.persist === false || this.readOnly.has(name)) {
      return;
    }
    const service = this.getService(name);
    if (!this.getStore(service)) {
      return;
    }
    const namespace = this.getNamespace(name);
    const bucket = `${service}|${namespace}`;
    this.pending[bucket] = this.pending[bucket] || { service, namespace, names: new Set() };
    this.pending[bucket].names.add(name);
  },

  // Coalesce the writes from one change into a single save per service. Without
  // this, a page seeding twenty inputs would issue twenty enrollment saves and
  // race itself.
  scheduleFlush: function () {
    if (this.flushTimeout !== null) {
      return;
    }
    this.flushTimeout = setTimeout(() => {
      this.flushTimeout = null;
      this.flush();
    }, 0);
  },

  // Merge the pending values into each service's stored namespace.
  //
  // Only the keys we computed are written, and never an undefined one. This is
  // the difference that matters: twine rewrote every key in its page's
  // `dataVars` list on each node transition, including keys the page only read,
  // so a stale read could overwrite another page's newer value.
  flush: function () {
    clearTimeout(this.flushTimeout);
    this.flushTimeout = null;

    Object.values(this.pending).forEach(({ service, namespace, names }) => {
      if (names.size === 0) {
        return;
      }

      const store = this.getStore(service);
      let stored;
      try {
        stored = Object.assign({}, store.getByDotString(namespace) || {});
      } catch (error) {
        console.error(`StateService: could not read '${namespace}' from ${service}: ${error.message}`);
        names.clear();
        return;
      }

      let wrote = false;
      names.forEach(name => {
        const key = this.getStorageKey(name);
        const value = this.values[name];
        if (value === undefined) {
          // Only an explicit `set(name, undefined)` gets this far - a
          // calculation that evaluates to undefined never marks itself pending,
          // so good data is never lost to a failed expression. This is a
          // learner clearing a field, and the stored key goes with it.
          if (Object.prototype.hasOwnProperty.call(stored, key)) {
            delete stored[key];
            wrote = true;
          }
          return;
        }
        stored[key] = value;
        wrote = true;
      });
      names.clear();

      if (!wrote) {
        return;
      }

      // A save that fails must not take the recompute down with it - this runs
      // from a timer, so an uncaught throw here surfaces as an unhandled error
      // with no page context. EnrollmentDataService.saveData in particular
      // reaches EF.enrollment without checking it exists.
      try {
        if (service === 'enrollment') {
          EnrollmentDataService.saveData(namespace, stored);
        } else {
          store.setItem(namespace, stored);
        }
      } catch (error) {
        console.error(`StateService: could not save '${namespace}' to ${service}: ${error.message}`);
      }
    });
  },

  // Helpers

  isEqual: function (a, b) {
    if (a === b) {
      return true;
    }
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }
    // Values here are the output of expressions - numbers, strings, arrays of
    // plain objects - so a structural compare is enough to tell a real change
    // from a recompute that landed on the same answer.
    return JSON.stringify(a) === JSON.stringify(b);
  },

  // Everything in the store, for debugging and for the course-wide variable
  // manifest a build-time pass can assemble from it.
  getSnapshot: function () {
    return {
      values: this.getAll(),
      definitions: Object.entries(this.definitions).reduce((snapshot, [name, definition]) => {
        snapshot[name] = {
          dependencies: definition.dependencies,
          expression: definition.expression,
          format: definition.format,
          service: this.getService(name),
          namespace: this.getNamespace(name),
          key: this.getStorageKey(name),
        };
        return snapshot;
      }, {}),
    };
  },
};

export default StateService;
export { SERVICES, SERVICE_ALIASES, DEFAULT_NAMESPACE };
